import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { NameCell } from '../../hooks/useNameMaps';
import {
  Alert,
  Button,
  Card,
  SearchableSelect,
  StatusBadge,
  Tabs,
  useToast,
  type SearchableSelectOption,
} from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import type { Category, CategoryCreate } from '@kss/api';
import { categoryService, storeService } from '@kss/api';
import NumberField from '../../components/NumberField';

type TabStatus = 'default' | 'danger' | 'success' | 'warning';

type FormState = {
  name: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  parentId: string;
  storeId: string;
};

const emptyForm: FormState = {
  name: '',
  description: '',
  sortOrder: 0,
  isActive: true,
  parentId: '',
  storeId: '',
};

const STEPS = ['basic', 'details', 'settings', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic info',
  details: 'Details',
  settings: 'Settings',
  review: 'Review & save',
};

const loadParentCategoryOptions = async (excludeId?: string): Promise<SearchableSelectOption[]> => {
  const rows = await categoryService.all();
  return (rows ?? [])
    .filter((c) => c.categoryId && c.categoryId !== excludeId)
    .map((c) => ({ value: c.categoryId!, label: c.name, description: c.description }));
};

const loadStoreOptions = async (): Promise<SearchableSelectOption[]> => {
  const rows = await storeService.all();
  return (rows ?? []).map((s) => ({
    value: s.storeId ?? '',
    label: s.name,
    description: s.code,
    disabled: !s.storeId,
  }));
};

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function CategoryForm() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';

  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('basic');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setLoadError(null);
        try {
          const rec = await categoryService.get(id);
          if (cancelled) return;
          setForm({
            name: rec.name ?? '',
            description: rec.description ?? '',
            sortOrder: Number(rec.sortOrder ?? 0),
            isActive: !!rec.isActive,
            parentId: rec.parentId ?? '',
            storeId: rec.storeId ?? '',
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else {
        setForm(emptyForm);
      }
      setErrors({});
      setStep('basic');
      setRedTabs(new Set());
    }
    run();
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const validateStep = (s: StepId): boolean => {
    const errs: typeof errors = {};
    if (s === 'basic' || s === 'review') {
      if (!form.name.trim()) errs.name = 'Name is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    const i = STEPS.indexOf(step);
    if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const next = STEPS[i + 1];
    setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
    setStep(next);
  };

  const goPrev = () => {
    const i = STEPS.indexOf(step);
    if (i === 0) return;
    setRedTabs((p) => new Set(p).add(step));
    setStep(STEPS[i - 1]);
  };

  const basicValid = !!form.name.trim();

  const handleSave = async () => {
    // Tab-wise validation: each tab only validates its own fields,
    // and Save & Next on intermediate tabs just advances. The API
    // save runs only on the final Review tab.
    if (!validateStep(step)) { toast.error('Fix errors', 'Some fields are invalid'); return; }
    const stepIdx = STEPS.indexOf(step);
    const isLastStep = stepIdx === STEPS.length - 1;
    if (!isLastStep) {
      const next = STEPS[stepIdx + 1];
      setRedTabs((p) => { const n = new Set(p); n.delete(next); return n; });
      setStep(next);
      return;
    }
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    setSaving(true);
    try {
      if (isEdit && id) {
        const payload: Category = {
          categoryId: id,
          name: form.name,
          description: form.description || undefined,
          sortOrder: Number(form.sortOrder),
          isActive: form.isActive,
          parentId: form.parentId || undefined,
          storeId: form.storeId || undefined,
        };
        await categoryService.update([payload]);
        toast.success('Saved', `${form.name} updated`);
      } else {
        const payload: CategoryCreate = {
          name: form.name,
          description: form.description || undefined,
          sortOrder: Number(form.sortOrder),
          isActive: form.isActive,
          parentId: form.parentId || undefined,
          storeId: form.storeId || undefined,
        };
        // Live `POST /api/Category` returns `data: true` instead of the
        // created entity (same backend bug as SalesOrder / Store /
        // Product — see BACKEND_AGENT_PROMPTS.md Item 4). Recover the
        // id with a follow-up filter so Save & Next can deep-link the
        // URL — but DON'T return early when it's missing, otherwise the
        // tab-advance code below never runs.
        await categoryService.create([payload]);
        toast.success('Saved', `${form.name} added`);
      }
      // Review-tab save succeeded — return to the grid list so the
      // user sees their new/edited row in context (matches edit-mode).
      navigate('/panel/categories');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => {
    const m: Record<string, TabStatus> = {};
    redTabs.forEach((s) => (m[s] = 'danger'));
    return m;
  }, [redTabs]);

  const field = (label: string, key: keyof FormState, input: React.ReactNode, required?: boolean) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">
        {label} {required && <span className="text-danger">*</span>}
      </span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  const basicStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field('Name', 'name',
        <input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Beverages" className={inputCls} />, true)}
      {field('Description', 'description',
        <input value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional" className={inputCls} />)}
    </div>
  );

  const detailsStep = (
    <div className="grid gap-3 md:grid-cols-2">
      <SearchableSelect
        label="Parent category"
        value={form.parentId}
        onChange={(v) => set('parentId', v)}
        loadOptions={() => loadParentCategoryOptions(isEdit ? id : undefined)}
        placeholder="Root (no parent)"
        searchPlaceholder="Search categories..."
      />
      {field('Sort order', 'sortOrder',
        <NumberField value={form.sortOrder} onValueChange={(n) => set('sortOrder', n)} className={inputCls} />)}
    </div>
  );

  const settingsStep = (
    <div className="grid gap-3 md:grid-cols-2">
      <SearchableSelect
        label="Store"
        value={form.storeId}
        onChange={(v) => set('storeId', v)}
        loadOptions={loadStoreOptions}
        placeholder="Select a store"
        searchPlaceholder="Search stores..."
      />
      {field('Status', 'isActive',
        <select value={form.isActive ? 'active' : 'inactive'} onChange={(e) => set('isActive', e.target.value === 'active')} className={selectCls}>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>)}
    </div>
  );

  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-sm font-medium">Ready to save</span>
      </div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Name" value={form.name} />
          <Row label="Description" value={form.description} />
          <Row label="Sort order" value={form.sortOrder} />
          <Row label="Status" value={<StatusBadge status={form.isActive ? 'active' : 'inactive'} />} />
          <Row label="Parent" value={<NameCell kind="category" id={form.parentId} />} />
          <Row label="Store" value={<NameCell kind="store" id={form.storeId} />} />
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic' as const, label: STEP_LABELS.basic, content: basicStep },
    { id: 'details' as const, label: STEP_LABELS.details, content: detailsStep },
    { id: 'settings' as const, label: STEP_LABELS.settings, content: settingsStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];

  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs
          tabs={tabs}
          activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c) setRedTabs((p) => { const n = new Set(p); for (let i = t + 1; i <= c; i++) n.add(STEPS[i]); return n; });
            else if (t > c) setRedTabs((p) => { const n = new Set(p); for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]); return n; });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus}
          barOnly
        />
      </div>
      <Card>
        <div className="min-h-[180px]">{loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}</div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            {!isLast && <Button type="button" onClick={goNext}>Next <ChevronRight className="h-4 w-4" /></Button>}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/categories')}>Cancel</Button>
            <Button type="button" onClick={handleSave} disabled={!basicValid || saving}>
              <Save className="h-4 w-4" />{' '}
              {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
